import { z } from 'zod'
import type { PipelineIcon } from '../../components/ep-pipeline-icon/ep-pipeline-icon';
import { isNullishOrWhitespace } from '../../functions/nullish';

export function FormatPropertyAddress(row: DAREVisualAssessmentImport): string {
  let result = `${row.STR_NUM} `;
  if (!isNullishOrWhitespace(row.STR_PFX)) {
    result += `${row.STR_PFX} `;
  }
  result += `${row.STR_NAME ?? '(no street name)'} `;
  if (!isNullishOrWhitespace(row.STR_SFX)) {
    result += `${row.STR_SFX} `;
  }

  return result.trim();
}

export type DAREVisualAssessmentImport = Record<string, any> & {
  ID: number;
  OBJECTID: string;
  GlobalID: string;
  HCAD_NUM: string;
  POINT_X: number;
  POINT_Y: number;
  /** Property Address */
  STR_PFX?: string; // not enforced and nullable
  STR_NUM: number; // has to be provided to parse
  STR_NAME?: string; // enforced by validation, but technically nullable
  STR_SFX?: string; // not enforced and nullable
  /** Mailing Address */
  MAIL_ADDR_1?: string;
  ImportFileName?: string;
  ImportComments?: string;
  Processed?: number;
  /** PermitApplMaster.ProjectNumber */
  EventID?: string;
  /** PermitApplMasterID */
  ImportedPamId?: number;
}

export interface DAREQueueItem {
  Row: DAREVisualAssessmentImport;
  ClientSelected: boolean;
  ClientProcessStatus: PipelineIcon;
  FailureReason: string;
  ClientValidationStatus: PipelineIcon;
  IsValid: boolean | null;
  HasNoErrors: boolean | null;
  Errors: FluentValidationFailure[];
}

export interface DAREImportResult {
  ID: number;
  FailureReason: string;
  Row: DAREVisualAssessmentImport | null;
}

/**
 * Error = 0,
 * Warning = 1,
 * Info = 2
 */
export type Severity = 0 | 1 | 2;

export interface FluentValidationFailure {
  PropertyName: string;
  ErrorMessage: string;
  AttemptedValue?: any;
  
  Severity: Severity;
}

export const SeverityDescription: Record<Severity, string> = {
  0: 'Error',
  1: 'Warning',
  2: 'Info',
};

export const SeverityEmoji: Record<Severity, string> = {
  0: '⛔',
  1: '⚠️',
  2: 'ℹ️',
};

export interface DAREValidationResult {
  ID: number;
  IsValid: boolean;
  HasNoErrors: boolean;
  Errors: FluentValidationFailure[];
}

export interface DAREApplicationType {
  ApplicationTypeID: number;
  ApplicationTypeName: string;
  IsValid: boolean;
  Errors: string[];
}

export const DAREExportOptionSchema = z.object({
  ImportFileName: z.string(),
  //ImportedDate: z.string().datetime({ offset: true }).nullable(),
  ImportedDate: z.string().pipe(z.coerce.date()).nullable(),
  HasUnprocessedRows: z.boolean(),
});

export type DAREExportOption = z.infer<typeof DAREExportOptionSchema>;

export const ApiErrorSchema = z.object({
  Message: z.string(),
  ExceptionMessage: z.string().optional(),
  ExceptionType: z.string().optional(),
  StackTrace: z.string().optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export class DAREImportAPI {
  baseURL: URL;
  constructor(base: URL) {
    this.baseURL = base;
  }

  private async _handleError(res: Response) {
    if (res.ok) return;

    let typedError: ApiError | undefined = undefined;
    try {
      const json = await res.json();
      typedError = await ApiErrorSchema.parseAsync(json);
    }
    catch {}
    if (typedError !== undefined) {
      throw typedError;
    }
    else {
      throw res;
    }
  }

  //#region Upload Queue
  public async ClearUploadQueue(): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/UploadQueue/Clear`, { method: 'POST' });
    await this._handleError(res);
    return res;
  }

  public async AddToUploadQueue(rawCsv: string, fileName: string, signal?: AbortSignal): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/UploadQueue/Add?${new URLSearchParams({ fileName, })}`, {
      method: 'POST',
      signal: signal,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(rawCsv),
    });
    return res;
  }

  public async GetUploadQueueViewHTML(): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/UploadQueue/View`);
    return res;
  }

  public async PromoteUploadQueue(): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/UploadQueue/Promote`, { method: 'POST' });
    await this._handleError(res);
    return res;
  }
  //#endregion

  //#region Import Queue
  public async GetUnprocessed(showCompletedSiblings: boolean = false): Promise<DAREVisualAssessmentImport[]> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/ImportQueue/GetUnprocessed?${new URLSearchParams({ showCompletedSiblings: showCompletedSiblings.toString() })}`);
    await this._handleError(res);
    return await res.json();
  }

  public async DoImport(ID: number, applicationTypeID: number): Promise<DAREImportResult> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/ImportQueue/DoImport?${new URLSearchParams({ ID: ID.toString(), applicationTypeID: applicationTypeID.toString()})}`, { method: 'POST' });
    await this._handleError(res);
    return await res.json();
  }

  public async ValidateRow(ID: number): Promise<DAREValidationResult> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/ImportQueue/Validate?${new URLSearchParams({ ID: ID.toString() })}`, { method: 'POST' });
    await this._handleError(res);
    return await res.json();
  }
  //#endregion

  public async GetApplicationTypes(): Promise<DAREApplicationType[]> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/GetApplicationTypes`);
    await this._handleError(res);
    return await res.json();
  }

  public async GetExportFileNames(): Promise<DAREExportOption[]> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/GetExportFileNames`);
    await this._handleError(res);
    const data = await res.json();
    const parsed = await DAREExportOptionSchema.array().safeParseAsync(data);
    if (parsed.success === false)
      throw parsed;
    return parsed.data;
  }

  public async DeleteRecord(ID: number): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/Delete?${new URLSearchParams({ ID: ID.toString() })}`, { method: 'DELETE' });
    return res;
  }

  public async ExportCSV(importFileName: string, deep: boolean = false, randomizeGlobalID: boolean = false): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/DAREImport/ExportCSV?${new URLSearchParams({ importFileName, deep: `${deep}`, randomizeGlobalID: `${randomizeGlobalID}` })}`);
    return res;
  }
}
